import React, { useEffect, useRef } from 'react';
import { scrollState, clamp } from '@/lib/depth';

/**
 * The cinematic intro + background hand-off, three fixed full-viewport layers.
 *
 *  z-0  hero3 — the cloud sea. The lasting background for every later section.
 *  z-1  hero2 — the floating cliff (a transparent cutout) sitting over hero3.
 *       The About composes around it. To hand off, the cliff grows and sweeps
 *       fully off-frame to the LEFT, leaving nothing but hero3.
 *  z-2  hero1 — the diorama room with a transparent circular window. On the
 *       first scroll it continuously enlarges (origin = the window) until it is
 *       entirely off-frame, revealing the cliff (hero2) over the clouds (hero3).
 *
 * Sequence by scroll depth:
 *   0 → ZOOM_END           hero1 zooms away → cliff (hero2) over clouds (hero3)
 *   ~ZOOM_END+             About layers in around the cliff
 *   H2_OUT_START→END       cliff grows + slides off-frame left → only hero3 left
 *   beyond                 Partnerships layers in around hero3
 */

const ZOOM_END = 2000; // scroll px at which hero1 is fully off-frame
const MAX_SCALE = 15.0; // large enough that the window fully fills the viewport (man fully off)
const H2_OUT_START = 3200; // cliff begins growing + sweeping off-frame left
const H2_OUT_END = 4200;

// Smooth cinematic acceleration — cubic ease in/out, not linear.
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const H3_OUT_START = 10000; // hero3 (clouds) fades as we descend to the threshold
const H3_OUT_END = 11200; // gone by "below, something older waits" — the ember world

export const DioramaZoom = () => {
  const fgRef = useRef(null); // hero1 (diorama)
  const h2Ref = useRef(null); // hero2 (cliff)
  const h3Ref = useRef(null); // hero3 (clouds)

  useEffect(() => {
    let raf;
    const mq = window.matchMedia('(max-width: 767px)');
    let isMobile = mq.matches;
    const onMq = (ev) => { isMobile = ev.matches; };
    mq.addEventListener('change', onMq);
    const tick = () => {
      const cam = scrollState.camera;

      // hero1 enlarges away from its window (origin 50%/33%) on first scroll
      const t = clamp(cam / ZOOM_END, 0, 1);
      const scale = 1 + (MAX_SCALE - 1) * easeInOutCubic(t);
      if (fgRef.current) fgRef.current.style.transform = `scale(${scale.toFixed(4)})`;

      // hero2 (cliff) — flying PAST it into the clouds. Scaling from an origin
      // to the RIGHT (toward the clouds we head into) makes it grow and drift
      // left *simultaneously* as one coupled motion (true dolly parallax),
      // rather than growing then sliding. Constant translate is just the rest
      // offset; only the scale animates.
      const e = easeInOutCubic(clamp((cam - H2_OUT_START) / (H2_OUT_END - H2_OUT_START), 0, 1));
      if (h2Ref.current) {
        if (isMobile) {
          // Portrait crops the cliff differently, so the desktop's fixed-origin
          // dolly reads as a zoom *into* it. Sweep the transform-origin rightward
          // as it scales so the focal point flies past the cliff toward the
          // clouds — the cliff slides slowly off-frame left while it enlarges.
          const ox = (50 + e * 46).toFixed(1);
          h2Ref.current.style.transformOrigin = `${ox}% 45%`;
          h2Ref.current.style.transform = `translateX(${(-e * 14).toFixed(1)}%) translateY(9%) scale(${(1 + e * 15).toFixed(3)})`;
        } else {
          h2Ref.current.style.transformOrigin = '72% 45%';
          h2Ref.current.style.transform = `translateX(-10%) translateY(10%) scale(${(1 + e * 15).toFixed(3)})`;
        }
        h2Ref.current.style.visibility = e >= 0.999 ? 'hidden' : 'visible';
      }

      // hero3 (clouds) fades out as we descend to the threshold, revealing the
      // dark ember world below
      const h3 = clamp(1 - (cam - H3_OUT_START) / (H3_OUT_END - H3_OUT_START), 0, 1);
      if (h3Ref.current) {
        h3Ref.current.style.opacity = h3.toFixed(3);
        h3Ref.current.style.visibility = h3 < 0.002 ? 'hidden' : 'visible';
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      mq.removeEventListener('change', onMq);
    };
  }, []);

  const base = process.env.PUBLIC_URL || '';
  return (
    <>
      {/* z-0 — hero3: the cloud-sea background, fades out at the threshold */}
      <img
        ref={h3Ref}
        src={`${base}/hero3-web.jpg`}
        alt=""
        aria-hidden="true"
        data-testid="diorama-hero3"
        className="fixed inset-0 h-full w-full object-cover"
        style={{ zIndex: 0, willChange: 'opacity' }}
      />
      {/* z-1 — hero2: the floating cliff cutout, over hero3, sweeps off left */}
      <img
        ref={h2Ref}
        src={`${base}/hero2-web.webp`}
        alt=""
        aria-hidden="true"
        data-testid="diorama-hero2"
        className="fixed inset-0 h-full w-full object-cover"
        style={{ zIndex: 1, transformOrigin: '72% 45%', willChange: 'transform' }}
      />
      {/* z-2 — hero1: the diorama, scales off-frame through its window */}
      <img
        ref={fgRef}
        src={`${base}/hero1-window.webp`}
        alt=""
        aria-hidden="true"
        data-testid="diorama-hero1"
        className="fixed inset-0 h-full w-full object-cover"
        style={{ zIndex: 2, transformOrigin: '50% 33%', willChange: 'transform' }}
      />
    </>
  );
};

export default DioramaZoom;
